/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Tab, Tabs, theme } from "@nordcloud/gnui";
import {
  useAddAwsCapacityMutation,
  useAddAzureCapacityMutation,
  useAddGcpCapacityMutation,
  ValidationError,
  AwsCapacityData,
  AzureEaCapacityData,
  GcpCapacityData,
  CapacityProvider,
} from "~/generated/graphql";
import { GlobalSpinner } from "~/components";
import { showError } from "~/services/toast";
import { isNotNil, noop } from "~/tools";
import { TAB_CONFIG } from "./constants";
import { AwsForm, AzureEAForm, GcpForm } from "./Forms";
import { ProviderForm, Provider } from "./Forms/ProviderForm";
import { SuccessScreen } from "./SuccessScreen";

export function CapacityForm() {
  const [step, setStep] = React.useState(0);
  const [validationErrors, setValidationErrors] = React.useState<
    ValidationError[]
  >([]);
  const [selectedProvider, setSelectedProvider] =
    React.useState<CapacityProvider | null>(null);

  const [addAwsCapacity, { loading: addAwsCapacityLoading }] =
    useAddAwsCapacityMutation();

  const [addAzureCapacity, { loading: addAzureCapacityLoading }] =
    useAddAzureCapacityMutation();

  const [addGcpCapacity, { loading: addGcpCapacityLoading }] =
    useAddGcpCapacityMutation();

  const goToPreviousStep = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const goToNextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handleAWSSubmit = async (data: AwsCapacityData) => {
    try {
      const res = await addAwsCapacity({ variables: { input: data } });

      if (res.data?.addAWSCapacity?.success) {
        goToNextStep();
      } else {
        const errors = res.data?.addAWSCapacity?.errors ?? [];
        setValidationErrors(errors.filter(isNotNil));
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const handleGCPSubmit = async (data: GcpCapacityData) => {
    try {
      const res = await addGcpCapacity({ variables: { input: data } });

      if (res.data?.addGCPCapacity?.success) {
        goToNextStep();
      } else {
        const errors = res.data?.addGCPCapacity?.errors ?? [];
        setValidationErrors(errors.filter(isNotNil));
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const handleAZURESubmit = async (data: AzureEaCapacityData) => {
    try {
      const res = await addAzureCapacity({ variables: { input: data } });

      if (res.data?.addAZURECapacity?.success) {
        goToNextStep();
      } else {
        const errors = res.data?.addAZURECapacity?.errors ?? [];
        setValidationErrors(errors.filter(isNotNil));
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const loading =
    addAwsCapacityLoading || addAzureCapacityLoading || addGcpCapacityLoading;

  return (
    <div
      css={{
        marginTop: theme.spacing.spacing04,
        "& i": {
          fontStyle: "italic",
        },
      }}
    >
      <GlobalSpinner loading={loading} />
      <Tabs step={step} handleTab={noop} wizard>
        <Tab
          key={TAB_CONFIG.provider.id}
          heading={TAB_CONFIG.provider.heading}
          disabled
        >
          <ProviderForm
            onSubmit={(provider) => {
              setSelectedProvider(mapProvider(provider));
              setStep((prevStep) => prevStep + 1);
            }}
          />
        </Tab>
        <Tab
          key={TAB_CONFIG.data.id}
          heading={TAB_CONFIG.data.heading}
          disabled
        >
          {selectedProvider === CapacityProvider.Aws && (
            <AwsForm
              onSubmit={({
                accountId,
                bucketId,
                reportName,
                reportPathPrefix,
                region,
              }) =>
                handleAWSSubmit({
                  accountId,
                  bucketId,
                  region,
                  reportName,
                  reportPathPrefix,
                })
              }
              onPrevStep={goToPreviousStep}
              validationErrors={validationErrors}
            />
          )}
          {selectedProvider === CapacityProvider.AzureEa && (
            <AzureEAForm
              onSubmit={(data) => {
                handleAZURESubmit({
                  enrollmentAccessKey: data.enrollment_access_key,
                  enrollmentNumber: data.enrollment_number,
                });
              }}
              onPrevStep={goToPreviousStep}
              validationErrors={validationErrors}
            />
          )}
          {selectedProvider === CapacityProvider.Gcp && (
            <GcpForm
              onSubmit={(data) => {
                handleGCPSubmit({
                  bigQueryDatasetName: data.bigquery_dataset_name,
                  bigQueryTableName: data.bigquery_table_name,
                  organizationId: data.organization_id,
                  projectName: data.project_name,
                  serviceAccountKey: JSON.stringify(data.service_account_key),
                  useDetailedBillingData: data.advanced_billing_data,
                });
              }}
              onPrevStep={goToPreviousStep}
              validationErrors={validationErrors}
            />
          )}
        </Tab>
        <Tab
          key={TAB_CONFIG.summary.id}
          heading={TAB_CONFIG.summary.heading}
          disabled
        >
          <SuccessScreen />
        </Tab>
      </Tabs>
    </div>
  );
}

function mapProvider(provider: Provider): CapacityProvider {
  switch (provider) {
    case Provider.AWS:
      return CapacityProvider.Aws;
    case Provider.AZURE_EA:
      return CapacityProvider.AzureEa;
    case Provider.GCP:
      return CapacityProvider.Gcp;
    default:
      throw new Error("Unknown provider");
  }
}
